import { Select, Spin } from 'antd';
import React, { useMemo, useState } from 'react';
import { useGetDocumentNoteTypeValuesQuery } from '@client/features/Datasets/services/api';
import type { DocumentFilterCondition } from '../../types';

interface NoteTypeSelectProps {
    condition: DocumentFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<DocumentFilterCondition>) => void;
}

/**
 * Multi-select for note types
 */
export const NoteTypeSelect: React.FC<NoteTypeSelectProps> = ({ condition, groupId, updateCondition }) => {
    const [noteTypeSearch, setNoteTypeSearch] = useState('');

    const { data: noteTypeData, isLoading: isNoteTypeLoading } = useGetDocumentNoteTypeValuesQuery({
        search: noteTypeSearch,
    });

    const noteTypeOptions = useMemo(
        () =>
            noteTypeData?.values?.map((v: any) => ({
                label: `${v.label}${v.count ? ` [${v.count} docs]` : ''}`,
                value: v.concept_id,
                title: v.label, // Store label for valueTitleMap
            })) || [],
        [noteTypeData]
    );

    const handleChange = (vals: (number | string)[] | undefined) => {
        if (!vals || vals.length === 0) {
            updateCondition(groupId, condition.id, { value: undefined, valueTitleMap: undefined });
            return;
        }

        // Build valueTitleMap from selected values
        const valueTitleMap: Record<string, string> = {};
        vals.forEach(val => {
            const option = noteTypeOptions.find(opt => opt.value === val);
            if (option && option.title) {
                valueTitleMap[String(val)] = option.title;
            }
        });

        updateCondition(groupId, condition.id, {
            value: vals,
            valueTitleMap: Object.keys(valueTitleMap).length > 0 ? valueTitleMap : undefined,
        });
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <Select
                mode="multiple"
                placeholder="Search note types..."
                className="w-full [&_.ant-select-selection-item-content]:!max-w-[120px] [&_.ant-select-selector]:!min-h-[32px]"
                allowClear
                showSearch
                filterOption={false}
                onSearch={setNoteTypeSearch}
                options={noteTypeOptions}
                loading={isNoteTypeLoading}
                value={condition.value}
                onChange={handleChange}
                notFoundContent={isNoteTypeLoading ? <Spin size="small" /> : 'No note types found'}
            />
        </div>
    );
};
